<template>
  <nav class="inline-sidebar" :class="{ collapsed: isCollapsed }" aria-label="侧边导航">
    <!-- 搜索栏 + 添加按钮 -->
    <div class="sidebar-header">
      <a-input v-if="!isCollapsed" v-model="searchKeyword" class="search-input" placeholder="请搜索看板..." allow-clear
        size="small">
        <template #prefix>
          <icon-search />
        </template>
      </a-input>
      <a-tooltip content="新建看板" position="right" mini>
        <div class="add-btn" role="button" tabindex="0" @click="handleAddDashboard" @keydown.enter.prevent="handleAddDashboard" @keydown.space.prevent="handleAddDashboard">
          <icon-plus class="add-icon" />
        </div>
      </a-tooltip>
    </div>

    <!-- 菜单列表 -->
    <div class="menu-list">
      <!-- 搜索结果模式 -->
      <template v-if="searchKeyword.trim()">
        <div v-if="searchResults.length > 0" class="search-results">
          <div v-for="result in searchResults" :key="result.item.key" class="search-result-item"
            role="button" tabindex="0"
            @click="handleSearchResultClick(result.item)"
            @keydown.enter.prevent="handleSearchResultClick(result.item)"
            @keydown.space.prevent="handleSearchResultClick(result.item)">
            <span class="result-title">{{ result.item.title }}</span>
            <span class="result-breadcrumb">{{ result.breadcrumb.join(' / ') }}</span>
          </div>
        </div>
        <div v-else class="empty-result">
          <icon-search class="empty-icon" />
          <span>未找到相关菜单</span>
        </div>
      </template>

      <!-- 正常菜单模式 -->
      <template v-else>
        <div v-for="item in menuItems" :key="item.key" class="menu-item-group">
          <!-- 分组标题（可点击展开/收起） -->
          <div class="group-header" role="button" tabindex="0" @click="toggleLevel1(item.key)" @keydown.enter.prevent="toggleLevel1(item.key)" @keydown.space.prevent="toggleLevel1(item.key)">
            <span class="group-title">{{ item.title }}</span>
            <icon-down v-if="item.children && item.children.length > 0" class="expand-arrow"
              :class="{ rotated: expandedKeys.includes(item.key) }" />
          </div>

          <!-- 二级菜单（展开，仅非收起状态） -->
          <Transition name="submenu-expand">
            <div v-if="!isCollapsed && item.children && item.children.length > 0 && expandedKeys.includes(item.key)"
              class="submenu-list">
              <template v-for="child in item.children" :key="child.key">
                <!-- 有三级菜单的二级项 -->
                <div v-if="child.children && child.children.length > 0" class="submenu-group">
                  <div class="menu-item level-2 has-children"
                    :class="{ expanded: expandedLevel2Keys.includes(child.key) }" role="button" tabindex="0" @click="toggleLevel2(child.key)" @keydown.enter.prevent="toggleLevel2(child.key)" @keydown.space.prevent="toggleLevel2(child.key)">
                    <span class="menu-title">{{ child.title }}</span>
                    <icon-down class="expand-arrow small"
                      :class="{ rotated: expandedLevel2Keys.includes(child.key) }" />
                  </div>

                  <!-- 三级菜单 -->
                  <Transition name="submenu-expand">
                    <div v-if="expandedLevel2Keys.includes(child.key)" class="submenu-list level-3">
                      <div v-for="grandchild in child.children" :key="grandchild.key" class="menu-item level-3"
                        :class="{ active: activeMenuKey === grandchild.key }"
                        role="button" tabindex="0"
                        @click="handleMenuItemClick(grandchild)"
                        @keydown.enter.prevent="handleMenuItemClick(grandchild)"
                        @keydown.space.prevent="handleMenuItemClick(grandchild)">
                        <span class="menu-title">{{ grandchild.title }}</span>
                      </div>
                    </div>
                  </Transition>
                </div>

                <!-- 无三级菜单的二级项 -->
                <div v-else class="menu-item level-2" :class="{ active: activeMenuKey === child.key }"
                  role="button" tabindex="0"
                  @click="handleMenuItemClick(child)"
                  @keydown.enter.prevent="handleMenuItemClick(child)"
                  @keydown.space.prevent="handleMenuItemClick(child)">
                  <icon-caret-right class="item-arrow" />
                  <span class="menu-title">{{ child.title }}</span>
                </div>
              </template>
            </div>
          </Transition>
        </div>
      </template>
    </div>

  </nav>
</template>

<script setup lang="ts">
import { CHATBI_V3_MENU_KEYS, useFeature } from '@/composables/useFeature'
import {
  PRODUCT_MENU_MAP,
  INSIGHT_MENU,
  findDomainByRoute,
  type MenuItem,
} from '@/config/menuConfig'
import { isProductRoute } from '@/constants/routeConfig'
import { filterMenuTreeByAccess } from '@/router/permissions'
import { useAuthStore } from '@/stores/auth'
import IconCaretRight from '@arco-design/web-vue/es/icon/icon-caret-right'
import IconDown from '@arco-design/web-vue/es/icon/icon-down'
import IconPlus from '@arco-design/web-vue/es/icon/icon-plus'
import IconSearch from '@arco-design/web-vue/es/icon/icon-search'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const chatbiV3 = useFeature('chatbi_v3')
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// 定义 emits
const emit = defineEmits<{
  (e: 'collapse', collapsed: boolean): void
  (e: 'add-dashboard'): void
}>()

// 新建看板
function handleAddDashboard() {
  emit('add-dashboard')
}

// 根据路由 + meta.menuGroup 选择菜单数据（优先九域精确归属，再降级全量菜单）
const menuItems = computed(() => {
  let menu: MenuItem[]
  const path = route.path
  const menuGroup = (route.meta?.menuGroup as string | undefined) ?? undefined
  const domain = findDomainByRoute(path, menuGroup)
  if (domain) {
    menu = domain.menu
    return filterMenuTreeByAccess(menu, authStore.canAccessRoute)
  }

  if (isProductRoute(path)) {
    const prefix = Object.keys(PRODUCT_MENU_MAP).find(
      (p) => path === p || path.startsWith(p + '/'),
    )
    if (prefix) {
      menu = PRODUCT_MENU_MAP[prefix].menu
      return filterMenuTreeByAccess(menu, authStore.canAccessRoute)
    }
  }

  // ChatBI V3 下线（任务 37）：关闭时「我的看板」不显示
  return chatbiV3.value ? INSIGHT_MENU : INSIGHT_MENU.filter(item => !CHATBI_V3_MENU_KEYS.has(item.key))
})

// 侧边栏收起状态
const isCollapsed = ref(false)

// 切换收起状态（预留）
 
function _toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  emit('collapse', isCollapsed.value)
}

// 搜索关键词
const searchKeyword = ref('')

// 展开的一级菜单 keys（默认全部展开）
const expandedKeys = ref<string[]>([
  // 全域洞察菜单
  'my-dashboards',
  'project-space',
  // 平台菜单（与 GOVERNANCE_MENU 中的 key 对应）
  'agent-mgmt',
  'knowledge',
  'model-mgmt',
  'data-governance',
  'ops-center',
  'platform-mgmt'
])

// 展开的二级菜单 keys
const expandedLevel2Keys = ref<string[]>([])

// 当前激活的一级模块
const activeModule = ref<string>('')

// 当前激活的菜单项 key
const activeMenuKey = ref('')

// 搜索结果类型
interface SearchResult {
  item: MenuItem
  breadcrumb: string[]
}

// 递归搜索菜单项
function searchMenuItems(
  items: MenuItem[],
  keyword: string,
  breadcrumb: string[] = []
): SearchResult[] {
  const results: SearchResult[] = []
  const lowerKeyword = keyword.toLowerCase()

  for (const item of items) {
    const currentBreadcrumb = [...breadcrumb, item.title]

    // 如果有 route，检查标题是否匹配
    if (item.route && item.title.toLowerCase().includes(lowerKeyword)) {
      results.push({
        item,
        breadcrumb: currentBreadcrumb
      })
    }

    // 递归搜索子菜单
    if (item.children && item.children.length > 0) {
      const childResults = searchMenuItems(item.children, keyword, currentBreadcrumb)
      results.push(...childResults)
    }
  }

  return results
}

// 搜索结果
const searchResults = computed<SearchResult[]>(() => {
  const keyword = searchKeyword.value.trim()
  if (!keyword) return []
  return searchMenuItems(menuItems.value, keyword)
})

// 点击搜索结果
function handleSearchResultClick(item: MenuItem) {
  if (item.route) {
    activeMenuKey.value = item.key
    router.push(item.route)
    searchKeyword.value = ''
  }
}

// 切换一级菜单展开状态
function toggleLevel1(key: string) {
  const index = expandedKeys.value.indexOf(key)
  if (index > -1) {
    expandedKeys.value.splice(index, 1)
  } else {
    expandedKeys.value.push(key)
  }
}

// 切换二级菜单展开状态
function toggleLevel2(key: string) {
  const index = expandedLevel2Keys.value.indexOf(key)
  if (index > -1) {
    expandedLevel2Keys.value.splice(index, 1)
  } else {
    expandedLevel2Keys.value.push(key)
  }
}

// 处理菜单项点击
function handleMenuItemClick(item: MenuItem) {
  if (item.route) {
    activeMenuKey.value = item.key
    router.push(item.route)
  }
}

// 递归查找菜单项（暂未使用，保留供后续使用）
function _findMenuByRoute(items: MenuItem[], path: string): MenuItem | null {
  for (const item of items) {
    if (item.route === path) {
      return item
    }
    if (item.children) {
      const found = _findMenuByRoute(item.children, path)
      if (found) return found
    }
  }
  return null
}

// 递归查找菜单项及其父级 key
function findMenuWithParent(
  items: MenuItem[],
  path: string,
  parentKey?: string
): { item: MenuItem; parentKey?: string } | null {
  for (const item of items) {
    if (item.route === path) {
      return { item, parentKey }
    }
    if (item.children) {
      const found = findMenuWithParent(item.children, path, item.key)
      if (found) return found
    }
  }
  return null
}

// 根据路由自动定位当前模块
watch(
  () => route.path,
  (path) => {
    for (const item of menuItems.value) {
      if (item.route === path) {
        activeModule.value = item.key
        activeMenuKey.value = item.key
        return
      }
      if (item.children) {
        const found = findMenuWithParent(item.children, path)
        if (found) {
          activeModule.value = item.key
          activeMenuKey.value = found.item.key
          // 自动展开对应的一级菜单
          if (!expandedKeys.value.includes(item.key)) {
            expandedKeys.value.push(item.key)
          }
          // 自动展开对应的二级菜单（如果有父级）
          if (found.parentKey && !expandedLevel2Keys.value.includes(found.parentKey)) {
            expandedLevel2Keys.value.push(found.parentKey)
          }
          return
        }
      }
    }
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
// ==========================================
// 火山引擎 / Arco Pro 风格侧边栏样式
// ==========================================

// 颜色变量（Claude 素色：侧边栏用 subtle 比主区深一档，视觉对比明显）
$color-bg: var(--c-bg-subtle);
$color-bg-hover: var(--c-bg-hover);
$color-bg-active: var(--c-accent-bg);
$color-text-primary: var(--color-text-primary);
$color-text-secondary: var(--color-text-secondary);
$color-text-tertiary: var(--color-text-tertiary);
$color-primary: var(--color-primary);
$color-border: var(--color-border);

.inline-sidebar {
  width: 220px;
  height: 100%;
  background: $color-bg;
  border-right: 1px solid $color-border;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  // 收起状态
  &.collapsed {
    width: 64px;

    .sidebar-header {
      justify-content: center;
      padding: 12px 8px;
    }
  }
}

// 顶部区域（搜索 + 添加按钮）
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  border-bottom: 1px solid $color-border;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  min-width: 0;

  :deep(.arco-input-wrapper) {
    background: var(--color-bg-container);
    border-color: transparent;
    border-radius: 16px;
    transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      background: $color-bg-hover;
    }

    &.arco-input-focus {
      background: var(--color-bg-elevated);
      border-color: $color-primary;
      box-shadow: 0 0 0 2px color-mix(in oklch, var(--accent) 20%, transparent);
    }
  }

  :deep(.arco-input-prefix) {
    color: $color-text-tertiary;
  }
}

// 菜单列表
.menu-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;

  // 美化滚动条
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 2px;

    &:hover {
      background: var(--hairline);
    }
  }
}

// 菜单项组
.menu-item-group {
  margin-bottom: 4px;
}

// 分组标题 - 可点击展开/收起
.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  margin: 0 8px;
  border-radius: 6px;
  color: $color-text-primary;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;

  &:hover {
    background: $color-bg-hover;
  }

  .group-title {
    flex: 1;
  }

  .expand-arrow {
    font-size: 12px;
    color: $color-text-tertiary;
    transition: transform 0.2s ease;

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

// 菜单项基础样式 - 圆角矩形 + 左右留 margin
.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  margin: 2px 12px; // 关键：左右留出 margin，不贴边
  border-radius: 6px; // 圆角矩形
  color: $color-text-secondary; // 未选中文字颜色加深
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
  background: transparent; // 透明背景

  // Hover 状态 - 圆角矩形
  &:hover {
    background: $color-bg-hover;
    color: $color-text-primary;
  }

  // 选中状态 - 圆角矩形 + 主题色背景
  &.active {
    background: $color-bg-active;
    color: $color-primary;
    font-weight: 500; // 选中项加粗

    .menu-icon,
    .item-arrow {
      color: $color-primary;
    }
  }
}

// 一级菜单
.menu-item.level-1 {
  .menu-item-content {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  .menu-icon {
    font-size: 16px;
    color: $color-text-tertiary;
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  .menu-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover .menu-icon {
    color: $color-text-primary;
  }

  &.active .menu-icon {
    color: $color-primary;
  }
}

// 展开箭头
.expand-arrow {
  font-size: 12px;
  color: $color-text-tertiary;
  transition: transform 0.2s ease, color 0.15s ease;
  flex-shrink: 0;

  &.rotated {
    transform: rotate(180deg);
  }

  &.small {
    font-size: 10px;
  }
}

// 二级菜单列表
.submenu-list {
  overflow: hidden;
  background: transparent;
  margin: 0;
  padding: 4px 0;
}

// 二级菜单项 - 层级缩进
.menu-item.level-2 {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  padding-left: 24px; // 层级缩进
  margin: 2px 12px;
  border-radius: 6px;
  font-size: 13px;
  background: transparent;

  .item-arrow {
    font-size: 10px;
    color: $color-text-tertiary;
    flex-shrink: 0;
  }

  .item-icon {
    font-size: 14px;
    color: $color-primary;
    flex-shrink: 0;
  }

  .menu-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.has-children {
    padding-right: 12px;
  }

  &:hover {
    background: $color-bg-hover;
    color: $color-text-primary;
  }

  &.active {
    background: $color-bg-active;
    color: $color-primary;
    font-weight: 500;
  }

  &.expanded {
    background: $color-bg-hover;
  }
}

// 二级菜单组（含三级）
.submenu-group {
  margin-bottom: 2px;
}

// 三级菜单列表
.submenu-list.level-3 {
  margin: 0;
  padding: 2px 0;
  background: transparent;
}

// 三级菜单项 - 更深的缩进
.menu-item.level-3 {
  padding: 6px 16px;
  padding-left: 40px; // 三级缩进
  margin: 2px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: $color-text-tertiary;
  background: transparent;

  &:hover {
    background: $color-bg-hover;
    color: $color-text-primary;
  }

  &.active {
    background: $color-bg-active;
    color: $color-primary;
    font-weight: 500;
  }
}

// 搜索结果
.search-results {
  padding: 4px 0;
}

.search-result-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 16px;
  margin: 2px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: $color-bg-hover;
  }

  .result-title {
    font-size: 13px;
    color: $color-text-primary;
  }

  .result-breadcrumb {
    font-size: 11px;
    color: $color-text-tertiary;
  }
}

// 空结果
.empty-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: $color-text-tertiary;

  .empty-icon {
    font-size: 32px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  span {
    font-size: 13px;
  }
}

// 子菜单展开动画
.submenu-expand-enter-active,
.submenu-expand-leave-active {
  transition: opacity 0.2s ease, max-height 0.2s ease;
  max-height: 500px;
}

.submenu-expand-enter-from,
.submenu-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

// 添加按钮
.add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
  background: $color-primary;
  color: var(--color-bg-elevated);
  flex-shrink: 0;

  &:hover {
    background: var(--c-accent-hover);
  }

  &:active {
    background: var(--c-accent-active);
  }

  .add-icon {
    font-size: 14px;
  }
}
</style>
